import {keyframes, css} from 'styled-components';

// Keyframes for subtle gradient animations
export const gradientShift = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

export const gradientPulse = keyframes`
  0%, 100% {
    background-size: 200% 200%;
    background-position: 0% 50%;
  }
  50% {
    background-size: 220% 220%;
    background-position: 100% 50%;
  }
`;

export const gradientFloat = keyframes`
  0%, 100% {
    background-position: 0% 0%;
  }
  25% {
    background-position: 100% 0%;
  }
  50% {
    background-position: 100% 100%;
  }
  75% {
    background-position: 0% 100%;
  }
`;

// Beautiful gradient variants - all easy on the eyes with low opacity
export const gradients = {
  // Original blue-purple
  oceanBreeze: `linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.1) 0%,
    rgba(147, 51, 234, 0.1) 100%
  )`,

  // Soft pink-orange sunset
  sunsetGlow: `linear-gradient(
    135deg,
    rgba(251, 113, 133, 0.08) 0%,
    rgba(249, 115, 22, 0.08) 50%,
    rgba(245, 158, 11, 0.08) 100%
  )`,

  // Cool mint-teal
  mintFresh: `linear-gradient(
    135deg,
    rgba(34, 197, 94, 0.08) 0%,
    rgba(6, 182, 212, 0.08) 50%,
    rgba(59, 130, 246, 0.08) 100%
  )`,

  // Warm purple-pink
  lavenderDream: `linear-gradient(
    135deg,
    rgba(139, 92, 246, 0.08) 0%,
    rgba(219, 39, 119, 0.08) 50%,
    rgba(236, 72, 153, 0.08) 100%
  )`,

  // Golden hour
  goldenHour: `linear-gradient(
    135deg,
    rgba(245, 158, 11, 0.08) 0%,
    rgba(251, 191, 36, 0.08) 50%,
    rgba(254, 240, 138, 0.08) 100%
  )`,

  // Ocean depths
  oceanDepth: `linear-gradient(
    135deg,
    rgba(30, 58, 138, 0.08) 0%,
    rgba(29, 78, 216, 0.08) 30%,
    rgba(59, 130, 246, 0.08) 60%,
    rgba(125, 211, 252, 0.08) 100%
  )`,

  // Forest whisper
  forestWhisper: `linear-gradient(
    135deg,
    rgba(21, 128, 61, 0.08) 0%,
    rgba(34, 197, 94, 0.08) 40%,
    rgba(74, 222, 128, 0.08) 80%,
    rgba(134, 239, 172, 0.08) 100%
  )`,

  // Cosmic night
  cosmicNight: `linear-gradient(
    135deg,
    rgba(30, 27, 75, 0.08) 0%,
    rgba(88, 28, 135, 0.08) 30%,
    rgba(147, 51, 234, 0.08) 60%,
    rgba(196, 181, 253, 0.08) 100%
  )`,
};

// Animated gradient variants with longer backgrounds for smooth animation
export const animatedGradients = {
  oceanBreeze: `linear-gradient(
    -45deg,
    rgba(59, 130, 246, 0.1) 0%,
    rgba(147, 51, 234, 0.1) 25%,
    rgba(59, 130, 246, 0.08) 50%,
    rgba(147, 51, 234, 0.08) 75%,
    rgba(59, 130, 246, 0.1) 100%
  )`,

  sunsetGlow: `linear-gradient(
    -45deg,
    rgba(251, 113, 133, 0.08) 0%,
    rgba(249, 115, 22, 0.08) 25%,
    rgba(245, 158, 11, 0.08) 50%,
    rgba(251, 113, 133, 0.06) 75%,
    rgba(249, 115, 22, 0.08) 100%
  )`,

  mintFresh: `linear-gradient(
    -45deg,
    rgba(34, 197, 94, 0.08) 0%,
    rgba(6, 182, 212, 0.08) 25%,
    rgba(59, 130, 246, 0.08) 50%,
    rgba(34, 197, 94, 0.06) 75%,
    rgba(6, 182, 212, 0.08) 100%
  )`,

  lavenderDream: `linear-gradient(
    -45deg,
    rgba(139, 92, 246, 0.08) 0%,
    rgba(219, 39, 119, 0.08) 25%,
    rgba(236, 72, 153, 0.08) 50%,
    rgba(139, 92, 246, 0.06) 75%,
    rgba(219, 39, 119, 0.08) 100%
  )`,

  goldenHour: `linear-gradient(
    -45deg,
    rgba(245, 158, 11, 0.08) 0%,
    rgba(251, 191, 36, 0.08) 25%,
    rgba(254, 240, 138, 0.08) 50%,
    rgba(245, 158, 11, 0.06) 75%,
    rgba(251, 191, 36, 0.08) 100%
  )`,
};

// CSS properties for animated gradients
export const getAnimatedGradientStyles = (
  gradientName: keyof typeof animatedGradients,
) => css`
  background: ${animatedGradients[gradientName]};
  background-size: 400% 400%;
  animation: ${gradientShift} 8s ease-in-out infinite;
`;

export const getFloatingGradientStyles = (
  gradientName: keyof typeof animatedGradients,
) => css`
  background: ${animatedGradients[gradientName]};
  background-size: 300% 300%;
  animation: ${gradientFloat} 12s ease-in-out infinite;
`;

export const getPulsingGradientStyles = (
  gradientName: keyof typeof animatedGradients,
) => css`
  background: ${animatedGradients[gradientName]};
  background-size: 200% 200%;
  animation: ${gradientPulse} 6s ease-in-out infinite;
`;
